import React, { useMemo } from 'react'
import DOMPurify from 'dompurify'
import CopyToClipboard from 'react-copy-to-clipboard'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { dateFormatWithTimeOptions } from '../../../config/dateFormatterConfig'
import ROUTES from '../../../config/routes'
import { useNewsAttachmentUrls } from '../api/fetchNewsAttachmentUrl'
import { parseAttachmentReference, referencedAttachmentIds, rewriteImageSources } from '../models/newsInlineImages'
import { NewsPost } from '../models/types'
import NewsAttachmentList from './NewsAttachmentList'

interface NewsFeedItemProps {
  post: NewsPost
  isUnread: boolean
  isAdmin: boolean
  isHighlighted: boolean
  onSetPinned: (id: number, isPinned: boolean) => void
  onEdit: (post: NewsPost) => void
  onDelete: (id: number) => void
}

function NewsFeedItem({
  post,
  isUnread,
  isAdmin,
  isHighlighted,
  onSetPinned,
  onEdit,
  onDelete,
}: Readonly<NewsFeedItemProps>) {
  const { t } = useTranslation()
  // FORBID_ATTR rather than DOMPurify.addHook: the hook API mutates a module singleton shared with
  // the copilot's markdown renderer. Without `target`, links open in the same tab and `rel` is moot.
  const sanitizedBody = useMemo(
    () => DOMPurify.sanitize(post.body, { USE_PROFILES: { html: true }, FORBID_ATTR: ['target'] }),
    [post.body],
  )
  const referencedIds = useMemo(() => referencedAttachmentIds(sanitizedBody, post.id), [sanitizedBody, post.id])
  const { urls } = useNewsAttachmentUrls(post.id, referencedIds)
  // Inline images are attachment references swapped for fresh links; one that points elsewhere, is
  // gone or has no link (yet) is left out. Only server-issued links are inserted, after sanitizing.
  const renderedBody = useMemo(() => {
    const attachmentIds = new Set(post.attachments.map((attachment) => attachment.id))
    return rewriteImageSources(sanitizedBody, (src) => {
      if (src === '') {
        return null
      }
      const reference = parseAttachmentReference(src)
      if (reference === null) {
        return src
      }
      if (reference.postId !== post.id || !attachmentIds.has(reference.attachmentId)) {
        return null
      }
      return urls.get(reference.attachmentId) ?? null
    }, { noReferrer: true })
  }, [sanitizedBody, post.id, post.attachments, urls])

  const itemClassName = [
    'news-feed__item',
    post.isPinned ? 'news-feed__item--pinned' : '',
    isUnread ? 'news-feed__item--unread' : '',
    isHighlighted ? 'news-feed__item--highlighted' : '',
  ].filter(Boolean).join(' ')

  return (
    <article className={itemClassName}>
      <div className="news-feed__item-header">
        <h2 className="news-feed__item-title">
          {post.isPinned && <i className="fas fa-thumbtack me-2" title={t('news.pinned')} />}
          {post.title}
          {isUnread && (
            <span className="news-feed__item-badge ms-2" data-testid="news-new-badge">{t('news.newBadge')}</span>
          )}
        </h2>
        <span className="news-feed__item-date">
          {new Intl.DateTimeFormat('et', dateFormatWithTimeOptions).format(new Date(post.createdAt))}
        </span>
        <div className="news-feed__item-actions">
          {/* Sharing is a reader's action, not an admin's — whoever wants to pass an announcement on
              is the person reading it. */}
          <CopyToClipboard
            text={`${process.env.REACT_APP_URL}/${ROUTES.news}/${post.id}`}
            onCopy={() => toast.success(t('news.linkCopied'))}
          >
            <button
              type="button"
              className="btn btn-link btn-sm px-1"
              title={t('news.copyLink')}
              data-testid="news-copy-link"
            >
              <i className="fal fa-link" />
            </button>
          </CopyToClipboard>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-link btn-sm px-1"
              title={post.isPinned ? t('news.unpin') : t('news.pin')}
              onClick={() => onSetPinned(post.id, !post.isPinned)}
              data-testid="news-pin"
            >
              <i className={post.isPinned ? 'fas fa-thumbtack' : 'fal fa-thumbtack'} />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-link btn-sm px-1"
              title={t('buttons.edit')}
              onClick={() => onEdit(post)}
              data-testid="news-edit"
            >
              <i className="fal fa-pen" />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-link btn-sm px-1"
              title={t('buttons.delete')}
              onClick={() => onDelete(post.id)}
              data-testid="news-delete"
            >
              <i className="fal fa-trash-alt" />
            </button>
          )}
        </div>
      </div>
      <div
        className="news-feed__item-body"
        // eslint-disable-next-line react/no-danger -- sanitized above with DOMPurify
        dangerouslySetInnerHTML={{ __html: renderedBody }}
      />
      <NewsAttachmentList postId={post.id} attachments={post.attachments} />
    </article>
  )
}

export default NewsFeedItem
